import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { mockLeads } from '@/mocks/leads';

export interface LiveLead {
  id: string;
  created_at: string;
  status: string;
  routing_status: string;
  vertical_id: string;
  geo_id: string;
  property_id: string;
  assigned_partner_id: string | null;
  // Mapped from raw_payload
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  postcode: string;
  city: string;
  country_code: string;
  intake_data: Record<string, unknown>;
  estimated_min_eur: number | null;
  estimated_max_eur: number | null;
  // Raw payload — kept for source detection and any field not yet promoted to a top-level column
  raw_payload: Record<string, unknown>;
  // Optional fields present in mock data / richer DB rows
  quality_score?: number | null;
  fraud_signals?: string[];
  quality_flags?: string[];
  updated_at?: string | null;
  assigned_offer_id?: string | null;
  lead_token?: string | null;
  operator_notes?: string | null;
  deleted_at?: string | null;
  signed_at?: string | null;
}

function mapRawLead(row: any): LiveLead {
  const p = row.raw_payload || {};
  return {
    id: row.id,
    created_at: row.created_at,
    status: row.status || 'new',
    routing_status: row.routing_status || 'pending',
    vertical_id: row.vertical_id || 'home_services',
    geo_id: row.geo_id || 'NL',
    property_id: row.property_id || 'pvc-vloer-nl',
    assigned_partner_id: row.assigned_partner_id || null,
    firstname: p.voornaam   || p.firstname  || '',
    lastname:  p.achternaam || p.lastname   || '',
    email:     p.email      || p.EMAIL      || '',
    phone:     p.telefoon   || p.phone      || p.PHONE || '',
    postcode: p.postcode || '',
    city: p.city || p.stad || '',
    country_code: p.country_code || 'NL',
    // Merge nested intake_data with top-level Facebook flat fields so both lead types populate
    intake_data: {
      floor_type:     p.vloertype || p.interesse || p.interest || '',
      surface_area_m2: p.oppervlakte || p.surface || '',
      ...(p.intake_data as Record<string, unknown> || {}),
    },
    raw_payload: p,
    estimated_min_eur: p.estimated_min_eur ?? null,
    estimated_max_eur: p.estimated_max_eur ?? null,
    quality_score: row.quality_score ?? null,
    fraud_signals: row.fraud_signals ?? [],
    quality_flags: row.quality_flags ?? [],
    updated_at: row.updated_at ?? null,
    assigned_offer_id: row.assigned_offer_id ?? null,
    lead_token: row.lead_token ?? null,
    operator_notes: row.operator_notes ?? null,
    deleted_at: row.deleted_at ?? null,
    signed_at: row.signed_at ?? null,
  };
}

export function useLeads(partnerId?: string | null) {
  const [leads, setLeads] = useState<LiveLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = () => setTick(t => t + 1);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      const mapped: LiveLead[] = mockLeads.map(m => ({
        id: m.id,
        created_at: m.created_at,
        status: m.status || 'new',
        routing_status: m.routing_status || 'pending',
        vertical_id: m.vertical_id,
        geo_id: m.geo_id,
        property_id: m.property_id,
        assigned_partner_id: m.assigned_partner_id || null,
        firstname: m.firstname || '',
        lastname: m.lastname || '',
        email: m.email || '',
        phone: m.phone || '',
        postcode: m.postcode || '',
        city: m.city || '',
        country_code: m.country_code || 'NL',
        intake_data: (m.intake_data as Record<string, unknown>) || {},
        raw_payload: (m as any).raw_payload || {},
        estimated_min_eur: (m as any).estimated_min_eur ?? null,
        estimated_max_eur: (m as any).estimated_max_eur ?? null,
        quality_score: m.quality_score ?? null,
        fraud_signals: m.fraud_signals ?? [],
        quality_flags: m.quality_flags ?? [],
        updated_at: m.updated_at ?? null,
        assigned_offer_id: m.assigned_offer_id ?? null,
        lead_token: m.lead_token ?? null,
        operator_notes: m.operator_notes ?? null,
        deleted_at: m.deleted_at ?? null,
        signed_at: m.signed_at ?? null,
      }));
      setLeads(mapped);
      setLoading(false);
      return;
    }

    const fetchLeads = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('leads')
          .select('*')
          .neq('status', 'submitted')
          .neq('status', 'routing')
          .order('created_at', { ascending: false })
          .limit(500);

        if (partnerId) {
          query = (query as any).eq('assigned_partner_id', partnerId);
        }

        const { data, error: err } = await query;
        if (err) throw err;
        setLeads((data || []).map(mapRawLead));
      } catch (e: any) {
        setError(e.message);
        setLeads([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
    window.addEventListener('focus', fetchLeads);
    return () => window.removeEventListener('focus', fetchLeads);
  }, [partnerId, tick]);

  return { leads, loading, error, refetch };
}
